import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getAllCompanies } from '../services/companyService';
import Footer from '../components/Footer';

const LandingPage = () => {
    const [stats, setStats] = useState({
        totalCompanies: 1000,
        totalJobSeekers: 3000,
        totalJobs: 5000,
        totalApplications: 20000
    });
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [activeVisionCard, setActiveVisionCard] = useState(null);

    useEffect(() => {
        fetchCompanies();
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const jobsSnapshot = await getDocs(collection(db, 'jobs'));
            const applicationsSnapshot = await getDocs(collection(db, 'applications'));
            const companiesSnapshot = await getDocs(collection(db, 'companies'));
            const usersSnapshot = await getDocs(collection(db, 'users'));

            setStats({
                totalCompanies: companiesSnapshot.size,
                totalJobSeekers: usersSnapshot.docs.filter(doc => doc.data().userType === 'jobseeker').length,
                totalJobs: jobsSnapshot.size,
                totalApplications: applicationsSnapshot.size
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const result = await getAllCompanies(10);
            if (result.success) {
                setCompanies(result.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const features = [
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            ),
            title: 'Cognitive Skill Alignment',
            description: 'Intelligent match engines examine skills, profiles, and historical outcomes to output the highest-fidelity recommendations.',
            gradient: 'from-purple-500 to-purple-600',
            bgGradient: 'from-purple-50 to-purple-100',
            color: 'purple'
        },
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            title: 'Rapid Submission Gateway',
            description: 'Apply to key positions in a single unified interaction. Your documents and resume variables are packaged automatically.',
            gradient: 'from-blue-500 to-blue-600',
            bgGradient: 'from-blue-50 to-blue-100',
            color: 'blue'
        },
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            title: 'Search Intelligence Logs',
            description: 'Monitor real-time employer engagement indexes, views, and active status indicators. Track conversion logs live.',
            gradient: 'from-green-500 to-green-600',
            bgGradient: 'from-green-50 to-green-100',
            color: 'green'
        },
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
            ),
            title: 'Real-time Priority Alerts',
            description: 'Instant notification channels ping you with custom-tailored matches immediately upon publication.',
            gradient: 'from-orange-500 to-orange-600',
            bgGradient: 'from-orange-50 to-orange-100',
            color: 'orange'
        },
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            title: 'Corporate Culture Diagnostics',
            description: 'Review complete employer background data, compensation index ratings, size, and operational summaries.',
            gradient: 'from-pink-500 to-pink-600',
            bgGradient: 'from-pink-50 to-pink-100',
            color: 'pink'
        },
        {
            icon: (
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            ),
            title: 'Professional Advancement Guild',
            description: 'Unlock structured resume templates, interview prep modules, and curated resource channels.',
            gradient: 'from-teal-500 to-teal-600',
            bgGradient: 'from-teal-50 to-teal-100',
            color: 'teal'
        }
    ];

    const howItWorks = [
        {
            step: '01',
            title: 'Build Digital Resume Portfolio',
            description: 'Initialize your credentials and structure a high-fidelity profile showcasing verified skills and targets.',
            icon: '👤'
        },
        {
            step: '02',
            title: 'Unlock High-Caliber Channels',
            description: 'Explore our catalog of vetted corporate requisitions or let matching filters scan target roles.',
            icon: '🔍'
        },
        {
            step: '03',
            title: 'Secure Career Engagements',
            description: 'Initiate proposals instantly, track interview indicators, and acquire your target position.',
            icon: '🎯'
        }
    ];

    const professionalImage = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop';

    const visionCards = [
        { id: 'vision', title: 'Our Vision', description: 'Elevating local professional talent through high-fidelity global standard pathways.', icon: '🌟', color: 'purple' },
        { id: 'mission', title: 'Our Mission', description: 'Fostering absolute visibility and seamless connections between expertise and openings.', icon: '🚀', color: 'blue' },
        { id: 'promise', title: 'Our Promise', description: 'Vetted, high-trust opportunities. Fully verified options, absolutely zero deceptive listings.', icon: '🛡️', color: 'green' },
        { id: 'goal', title: 'Our Goal', description: 'Catalyzing 100,000+ career milestones within our five-year roadmap.', icon: '📈', color: 'orange' }
    ];

    const handleVisionCardClick = (id) => {
        setActiveVisionCard(id);
        setTimeout(() => {
            setActiveVisionCard(null);
        }, 300);
    };

    // Social media links configuration
    const socialLinks = {
        twitter: 'https://twitter.com/harshaljobs',
        linkedin: 'https://www.linkedin.com/company/harshaljobs?_l=en_US',
        github: 'https://github.com/harshaljobs',
        instagram: 'https://instagram.com/harshaljobs'
    };

    // Footer link click handlers
    const handleSocialClick = (url) => {
        window.open(url, '_blank', 'noopener noreferrer');
    };

    return (
        <>
                        <style>{`
                /* Import fonts: Playfair Display for title, Poppins for subtitle, Inter for body */
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=Poppins:wght@600;700&family=Inter:wght@300;400;600;700&display=swap');

                :root {
                    --font-title: 'Playfair Display', serif;
                    --font-subtitle: 'Poppins', sans-serif;
                    --font-body: 'Inter', system-ui, -apple-system, sans-serif;
                    --primary-gradient: linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%);
                    --secondary-gradient: linear-gradient(135deg, #10B981 0%, #059669 100%);
                    --accent-gradient: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
                }
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
                body {
                    font-family: var(--font-body);
          background: #FFFFFF;
          color: #0F172A;
          overflow-x: hidden;
          cursor: auto;
        }
        
        /* Consistent heading font styles */
        h1, h2, h3, h4, h5, h6, .gradient-text, .premium-title, .stat-value, .feature-title, .journey-card h3 {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes floatSlow {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-25px);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes spin-slow-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }
        
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.3);
            opacity: 0;
          }
        }
        
        @keyframes countUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes cardFlash {
          0% {
            background-color: rgba(139, 92, 246, 0);
            border-color: rgba(139, 92, 246, 0);
          }
          50% {
            background-color: rgba(139, 92, 246, 0.15);
            border-color: rgba(139, 92, 246, 0.5);
          }
          100% {
            background-color: rgba(139, 92, 246, 0);
            border-color: rgba(139, 92, 246, 0);
          }
        }

        @keyframes cardFlashBlue {
          0% {
            background-color: rgba(59, 130, 246, 0);
            border-color: rgba(59, 130, 246, 0);
          }
          50% {
            background-color: rgba(59, 130, 246, 0.15);
            border-color: rgba(59, 130, 246, 0.5);
          }
          100% {
            background-color: rgba(59, 130, 246, 0);
            border-color: rgba(59, 130, 246, 0);
          }
        }

        @keyframes cardFlashGreen {
          0% {
            background-color: rgba(16, 185, 129, 0);
            border-color: rgba(16, 185, 129, 0);
          }
          50% {
            background-color: rgba(16, 185, 129, 0.15);
            border-color: rgba(16, 185, 129, 0.5);
          }
          100% {
            background-color: rgba(16, 185, 129, 0);
            border-color: rgba(16, 185, 129, 0);
          }
        }

        @keyframes cardFlashOrange {
          0% {
            background-color: rgba(245, 158, 11, 0);
            border-color: rgba(245, 158, 11, 0);
          }
          50% {
            background-color: rgba(245, 158, 11, 0.15);
            border-color: rgba(245, 158, 11, 0.5);
          }
          100% {
            background-color: rgba(245, 158, 11, 0);
            border-color: rgba(245, 158, 11, 0);
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s cubic-bezier(0.2, 0.9, 0.4, 1.1) forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.5s ease-out forwards;
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: floatSlow 6s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 12s linear infinite;
        }
        
        .animate-spin-slow-reverse {
          animation: spin-slow-reverse 12s linear infinite;
        }
        
        .animate-gradient {
          background-size: 200% auto;
          animation: gradientShift 5s ease infinite;
        }
        
        .hover-lift {
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .hover-lift:hover {
          transform: translateY(-6px);
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .gradient-text {
          background: linear-gradient(90deg, #8B5CF6, #3B82F6, #10B981);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientShift 5s ease infinite;
          font-weight: 700;
        }

                /* Hero-specific font utilities */
                .hero-title {
                    font-family: var(--font-title);
                    font-weight: 800;
                }

                .hero-subtitle {
                    font-family: var(--font-subtitle);
                    font-weight: 700;
                }

                .hero-para {
                    font-family: var(--font-body);
                    font-weight: 400;
                }
        
        .feature-card {
          position: relative;
          background: white;
          border-radius: 1.5rem;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
        }
        
        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #8B5CF6, #3B82F6, #10B981, #8B5CF6);
          background-size: 200% 100%;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.5s ease;
        }
        
        .feature-card:hover::before {
          transform: scaleX(1);
          animation: shimmer 2s ease infinite;
        }
        
        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 40px -12px rgba(0, 0, 0, 0.15);
          border-color: #c7d2fe;
        }

        .feature-icon-wrapper {
          width: 72px;
          height: 72px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .feature-card:hover .feature-icon-wrapper {
          transform: scale(1.05);
          box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.1);
        }

        .feature-title {
          transition: all 0.3s ease;
          font-weight: 700;
          letter-spacing: -0.01em;
        }

        .feature-card:hover .feature-title {
          background: linear-gradient(90deg, #8B5CF6, #3B82F6);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .stat-card {
          transition: all 0.3s ease;
        }
        
        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -12px rgba(0, 0, 0, 0.1);
        }

        .benefit-card {
          transition: all 0.3s ease;
        }
        
        .benefit-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -12px rgba(0, 0, 0, 0.1);
        }

        .testimonial-card {
          transition: all 0.3s ease;
        }
        
        .testimonial-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 30px -12px rgba(0, 0, 0, 0.12);
        }
        
        html {
          scroll-behavior: smooth;
        }
        
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #F1F5F9;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #94A3B8;
        }

        .feature-description {
          line-height: 1.6;
          font-size: 0.95rem;
          color: #475569;
        }

        .journey-card {
          transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
        }
        
        .journey-card:hover {
          transform: translateY(-4px);
        }

        .journey-icon-wrapper {
          transition: all 0.3s ease;
        }

        .journey-card:hover .journey-icon-wrapper {
          transform: scale(1.1);
        }

        .quote-mark {
          font-family: 'Georgia', serif;
          line-height: 1;
        }

        /* Premium Vision Cards Styles */
        .premium-vision-card {
          position: relative;
          background: linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.98));
          backdrop-filter: blur(10px);
          border-radius: 1.5rem;
          transition: all 0.3s ease;
          overflow: hidden;
          border: 1px solid rgba(139, 92, 246, 0.2);
          box-shadow: 0 8px 20px -6px rgba(0, 0, 0, 0.08);
          cursor: pointer;
        }
        
        .premium-vision-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 30px -12px rgba(139, 92, 246, 0.2);
          border-color: rgba(139, 92, 246, 0.4);
        }
        
        .premium-icon-wrapper {
          width: 70px;
          height: 70px;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          position: relative;
          z-index: 1;
        }
        
        .premium-vision-card:hover .premium-icon-wrapper {
          transform: scale(1.05);
        }
        
        .premium-title {
          background: linear-gradient(135deg, #1e293b, #334155);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          transition: all 0.3s ease;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        
        .premium-vision-card:hover .premium-title {
          background: linear-gradient(135deg, #8B5CF6, #3B82F6);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .premium-description {
          color: #475569;
          transition: color 0.3s ease;
        }
        
        .premium-vision-card:hover .premium-description {
          color: #334155;
        }
        
        .card-flash-purple {
          animation: cardFlash 0.3s ease-out;
        }
        
        .card-flash-blue {
          animation: cardFlashBlue 0.3s ease-out;
        }
        
        .card-flash-green {
          animation: cardFlashGreen 0.3s ease-out;
        }
        
        .card-flash-orange {
          animation: cardFlashOrange 0.3s ease-out;
        }

        /* Premium Stats Cards Styles */
        .premium-stat-card {
          position: relative;
          background: white;
          border-radius: 1.25rem;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        
        .premium-stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #8B5CF6, #3B82F6, #10B981, #8B5CF6);
          background-size: 200% 100%;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }
        
        .premium-stat-card:hover::before {
          transform: scaleX(1);
          animation: shimmer 2s ease infinite;
        }
        
        .premium-stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 30px -12px rgba(0, 0, 0, 0.15);
          border-color: #c7d2fe;
        }
        
        .stat-icon-wrapper {
          width: 56px;
          height: 56px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.34, 1.2, 0.64, 1);
        }
        
        .premium-stat-card:hover .stat-icon-wrapper {
          transform: scale(1.1) rotate(3deg);
        }
        
        .stat-value {
          font-size: 1.875rem;
          font-weight: 800;
          background: linear-gradient(135deg, #1e293b, #334155);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          transition: all 0.3s ease;
          letter-spacing: -0.02em;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        
        .premium-stat-card:hover .stat-value {
          background: linear-gradient(135deg, #8B5CF6, #3B82F6);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
          color: #64748b;
          font-weight: 500;
          transition: color 0.3s ease;
        }
        
        .premium-stat-card:hover .stat-label {
          color: #475569;
        }
        
        .stat-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 32px;
          height: 32px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.3s ease;
        }
        
        .premium-stat-card:hover .stat-badge {
          opacity: 1;
          transform: scale(1);
        }

        /* Vision Scroll Styles */
        .vision-scroll-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        
        .vision-scroll-container::-webkit-scrollbar {
          height: 6px;
        }
        
        .vision-scroll-container::-webkit-scrollbar-track {
          background: #eef2ff;
          border-radius: 10px;
        }
        
        .vision-scroll-container::-webkit-scrollbar-thumb {
          background: #a78bfa;
          border-radius: 10px;
        }
        
        .vision-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #8B5CF6;
        }
        
        .vision-track {
          display: flex;
          gap: 1.5rem;
          padding: 0.5rem 0 1.5rem 0;
          width: max-content;
        }
        
        .vision-scroll-btn {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: white;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        
        .vision-scroll-btn:hover {
          background: linear-gradient(135deg, #8B5CF6, #3B82F6);
          border-color: #8B5CF6;
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(139, 92, 246, 0.3);
        }
        
        .vision-scroll-btn:hover svg {
          stroke: white;
        }

        /* Features Scroll Styles */
        .features-scroll-container {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        
        .features-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        
        .features-scroll-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .features-scroll-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        
        .features-scroll-container::-webkit-scrollbar-thumb:hover {
          background: #8B5CF6;
        }
        
        .features-track {
          display: flex;
          gap: 1.5rem;
          padding: 0.5rem 0 1.5rem 0;
        }
        
        .feature-card-scroll {
          flex: 0 0 auto;
          width: 320px;
          background: white;
          border-radius: 1.5rem;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }
        
        .feature-card-scroll::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #8B5CF6, #3B82F6, #10B981, #8B5CF6);
          background-size: 200% 100%;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.5s ease;
        }
        
        .feature-card-scroll:hover::before {
          transform: scaleX(1);
          animation: shimmer 2s ease infinite;
        }
        
        .feature-card-scroll:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 40px -12px rgba(0, 0, 0, 0.15);
          border-color: #c7d2fe;
        }
        
        .scroll-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }
        
        .scroll-btn:hover {
          background: #8B5CF6;
          border-color: #8B5CF6;
          transform: scale(1.05);
        }
        
        .scroll-btn:hover svg {
          stroke: white;
        }
        
        @media (max-width: 768px) {
          .feature-card-scroll {
            width: 280px;
          }
          .vision-card-scroll {
            width: 280px;
          }
          .premium-icon-wrapper {
            width: 60px;
            height: 60px;
          }
          .stat-value {
            font-size: 1.5rem;
          }
          .stat-icon-wrapper {
            width: 48px;
            height: 48px;
          }
        }
        
        @keyframes float-subtle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }

        a, button, [role="button"], input, select, textarea {
          cursor: pointer;
        }

        /* Additional consistent heading styles */
        .text-3xl, .text-4xl, .text-5xl, .text-6xl, .text-7xl, .text-8xl {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
      `}</style>

            <div className="bg-white">
                {/* HERO SECTION */}
                <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)' }}>
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full filter blur-3xl animate-float"></div>
                        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full filter blur-3xl animate-float-slow"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-purple-400/10 to-blue-400/10 rounded-full filter blur-3xl animate-spin-slow"></div>
                        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)', backgroundSize: '40px 40px', opacity: 0.4 }}></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center z-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-purple-200 shadow-lg mb-8 animate-fadeInUp">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                            </span>
                            <span className="text-sm font-semibold text-purple-700">🚀 India's Premier Career Intelligence Gateway</span>
                        </div>

                        {/* Updated heading with smaller font */}
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 animate-fadeInUp hero-title" style={{ animationDelay: '0.1s' }}>
                            Unlock Your Peak
                            <span className="gradient-text block mt-2 hero-subtitle">Professional Potential.</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-12 leading-relaxed animate-fadeInUp hero-para" style={{ animationDelay: '0.2s' }}>
                            Establish direct channels with premier organizations. Access intelligence-driven matching, unified submissions, 
                            and elite career acceleration modules—all in one place.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
                            <Link 
                                to="/register" 
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-white rounded-xl overflow-hidden transition-all duration-300 hover:scale-105"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600"></div>
                                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <span className="relative flex items-center gap-2">
                                    Register Career Account
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </span>
                            </Link>
                            <Link 
                                to="/jobs" 
                                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all duration-300"
                            >
                                Browse Jobs
                            </Link>
                        </div>

                        <div className="flex flex-wrap justify-center gap-6 md:gap-12 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
                            {[
                                { value: stats.totalCompanies.toLocaleString() + '+', label: 'Vetted Corporations', icon: '🏢', color: 'purple' },
                                { value: stats.totalJobSeekers.toLocaleString() + '+', label: 'Active Candidates', icon: '👥', color: 'blue' },
                                { value: stats.totalJobs.toLocaleString() + '+', label: 'Verified Requisitions', icon: '💼', color: 'green' }
                            ].map((stat, idx) => (
                                <div key={idx} className="flex items-center gap-4 px-6 py-3 bg-white/60 backdrop-blur-sm rounded-full border border-slate-200 shadow-sm">
                                    <div className={`w-10 h-10 rounded-full bg-${stat.color}-100 flex items-center justify-center text-xl`}>
                                        {stat.icon}
                                    </div>
                                    <div className="text-left">
                                        <div className="text-xl font-bold text-slate-800">{stat.value}</div>
                                        <div className="text-xs text-slate-500 font-medium">{stat.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                        <div className="w-6 h-10 border-2 border-slate-300 rounded-full flex justify-center">
                            <div className="w-1 h-2 bg-purple-500 rounded-full mt-2 animate-pulse"></div>
                        </div>
                    </div>
                </section>

                {/* HOW IT WORKS SECTION */}
                <section className="py-24 bg-gradient-to-b from-white to-slate-50/50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 mb-6">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                                </span>
                                <span className="text-sm font-semibold text-purple-700">Sleek & Unbiased Process</span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
                                Procedural <span className="gradient-text">Pipeline</span>
                            </h2>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                Step-by-step methodology to elevate your professional profile and lock down elite placements.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8 relative">
                            <div className="hidden md:block absolute top-1/3 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-200 via-blue-200 to-purple-200 -z-0"></div>
                            
                            {howItWorks.map((item, index) => (
                                <div 
                                    key={index} 
                                    className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100 hover:border-purple-200 transform hover:-translate-y-2"
                                >
                                    <div className="absolute top-6 right-6">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500/10 to-blue-500/10 flex items-center justify-center">
                                            <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                                                {item.step}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="p-8 pt-12">
                                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-4xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-xl">
                                            {item.icon}
                                        </div>
                                        
                                        <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-purple-700 transition-colors">
                                            {item.title}
                                        </h3>
                                        
                                        <p className="text-slate-600 leading-relaxed">
                                            {item.description}
                                        </p>
                                    </div>
                                    
                                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/0 to-blue-50/0 group-hover:from-purple-50/30 group-hover:to-blue-50/30 transition-all duration-500 pointer-events-none"></div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-16 text-center">
                            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-md border border-slate-100">
                                <div className="flex -space-x-2">
                                    {[1, 2, 3, 4].map((i) => (
                                        <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-blue-400 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                                            {String.fromCharCode(64 + i)}
                                        </div>
                                    ))}
                                </div>
                                <span className="text-sm text-slate-600">
                                    <strong className="text-slate-800">10,000+</strong> professionals already hired
                                </span>
                                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FEATURES SECTION - WITH HORIZONTAL SCROLLING CARDS */}
                <section className="py-24 bg-white relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-50 rounded-full filter blur-3xl opacity-50"></div>
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-50 rounded-full filter blur-3xl opacity-50"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 mb-6 shadow-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                                </span>
                                <span className="text-sm font-semibold bg-gradient-to-r from-purple-700 to-blue-700 bg-clip-text text-transparent">
                                    Unified Executive Toolset
                                </span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
                                Everything Required to <span className="gradient-text">Excel</span>
                            </h2>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                High-fidelity career utilities crafted to accelerate professional advancement and secure target roles efficiently.
                            </p>
                        </div>

                        <div className="relative">
                            <button 
                                className="scroll-btn absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 hidden md:flex"
                                onClick={() => {
                                    const container = document.getElementById('featuresScrollContainer');
                                    if (container) container.scrollBy({ left: -340, behavior: 'smooth' });
                                }}
                            >
                                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            
                            <button 
                                className="scroll-btn absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 hidden md:flex"
                                onClick={() => {
                                    const container = document.getElementById('featuresScrollContainer');
                                    if (container) container.scrollBy({ left: 340, behavior: 'smooth' });
                                }}
                            >
                                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <div 
                                id="featuresScrollContainer"
                                className="features-scroll-container overflow-x-auto overflow-y-hidden"
                                style={{ scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
                            >
                                <div className="features-track" style={{ display: 'flex', gap: '1.5rem', padding: '0.5rem 0 1.5rem 0', width: 'max-content' }}>
                                    {features.map((feature, index) => (
                                        <div 
                                            key={index} 
                                            className="feature-card-scroll relative bg-white rounded-2xl transition-all duration-500"
                                            style={{ flex: '0 0 auto', width: '320px', background: 'white' }}
                                        >
                                            <div className="relative p-8 rounded-2xl transition-all duration-500 h-full bg-white">
                                                <div className={`feature-icon-wrapper bg-gradient-to-br ${feature.bgGradient} mb-6 shadow-lg border-2 border-${feature.color}-200`}>
                                                    <div className={`text-${feature.color}-600`}>
                                                        {feature.icon}
                                                    </div>
                                                </div>
                                                <h3 className={`feature-title text-2xl font-bold text-slate-800 mb-3 transition-colors`}>
                                                    {feature.title}
                                                </h3>
                                                <p className="feature-description text-slate-600 leading-relaxed">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WHY CHOOSE US SECTION - REDESIGNED & PREMIUM */}
                <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100/30 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full filter blur-3xl"></div>
                    
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
                        {/* Header with Badge */}
                        <div className="text-center mb-14">
                            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white shadow-md border border-purple-100 mb-5 backdrop-blur-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-600"></span>
                                </span>
                                <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-blue-700">
                                    ENDORSED BY TOP CORPORATIONS
                                </span>
                            </div>
                            
                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-800 mb-4">
                                Endorsed by Leading <br/>
                                <span className="gradient-text">Corporate Talent</span>
                            </h2>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                Align with thousands of elite candidates who have elevated their placement trajectory through our platform.
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            {/* LEFT SIDE - Redesigned stats and benefits */}
                            <div>
                                {/* Modern Stats Grid - New Design */}
                                <div className="grid grid-cols-2 gap-5 mb-8">
                                    {[
                                        { value: '10,000+', label: 'Verified Requisitions', icon: '💼', gradient: 'from-violet-500 to-purple-600', delay: '0s' },
                                        { value: '1,200+', label: 'Enterprise Partners', icon: '🏢', gradient: 'from-blue-500 to-cyan-600', delay: '0.1s' },
                                        { value: '98%', label: 'Successful Placements', icon: '🎯', gradient: 'from-emerald-500 to-teal-600', delay: '0.2s' },
                                        { value: '50k+', label: 'Onboarded Profiles', icon: '⭐', gradient: 'from-amber-500 to-orange-600', delay: '0.3s' }
                                    ].map((stat, idx) => (
                                        <div 
                                            key={idx} 
                                            className="group bg-white rounded-2xl p-5 border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                            style={{ animationDelay: stat.delay }}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center text-2xl shadow-md transform transition-transform group-hover:scale-110 duration-300`}>
                                                    {stat.icon}
                                                </div>
                                                <div>
                                                    <div className={`text-2xl font-extrabold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                                                        {stat.value}
                                                    </div>
                                                    <div className="text-slate-500 text-sm font-medium mt-0.5">{stat.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Premium Benefits List - Redesigned */}
                                <div className="space-y-4 mt-6">
                                    {[
                                        {
                                            title: 'Vetted Corporate Entities Only',
                                            desc: 'Strict verification criteria. We reject candidate-poor employer profiles to protect professional security.',
                                            icon: '🛡️',
                                            color: 'purple'
                                        },
                                        {
                                            title: 'High-Fidelity Security Protocols',
                                            desc: 'Full control over your visibility settings. Your resume details are securely protected.',
                                            icon: '🔒',
                                            color: 'blue'
                                        },
                                        {
                                            title: 'Advanced Professional Guidance',
                                            desc: 'Cognitive-powered evaluations, profile reviews, and direct advice from leading corporate figures.',
                                            icon: '🎓',
                                            color: 'green'
                                        }
                                    ].map((item, idx) => (
                                        <div 
                                            key={idx}
                                            className="group bg-white rounded-xl p-5 border border-slate-100 hover:border-purple-200 transition-all duration-300 hover:shadow-lg"
                                        >
                                            <div className="flex gap-4">
                                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${item.color}-100 to-${item.color}-200 flex items-center justify-center text-2xl flex-shrink-0 transition-transform group-hover:scale-110 duration-300`}>
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-800 mb-1 text-lg">{item.title}</h3>
                                                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Trust Badge */}
                                <div className="mt-8 flex items-center gap-4 pt-4 border-t border-slate-100">
                                    <div className="flex -space-x-3">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white flex items-center justify-center text-slate-700 text-sm font-bold shadow-sm">
                                                {String.fromCharCode(64 + i)}
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 24 24">
                                                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                                </svg>
                                            ))}
                                            <span className="text-slate-600 text-sm ml-2">4.9/5 from 5k+ reviews</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT SIDE - Premium Image Card */}
                            <div className="relative">
                                <div className="absolute -inset-3 bg-gradient-to-r from-purple-500/30 via-blue-500/30 to-purple-500/30 rounded-3xl blur-xl opacity-70 group-hover:opacity-100 transition duration-500"></div>
                                <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl border border-white/20">
                                    <div className="relative">
                                        <img 
                                            src={professionalImage} 
                                            alt="Professional team collaboration and career growth"
                                            className="w-full h-auto object-cover transition-all duration-700 hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                                        
                                        {/* Floating badge on image */}
                                        <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md rounded-xl p-4 shadow-lg border border-white/20">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-xs text-purple-600 font-semibold uppercase tracking-wide">Success Story</p>
                                                    <p className="text-slate-800 font-bold text-sm mt-0.5">"Found my dream job in just 2 weeks!"</p>
                                                </div>
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS SECTION */}
                <section className="py-24 bg-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #8B5CF6 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
                    
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-50 border border-purple-200 mb-6 shadow-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                                </span>
                                <span className="text-sm font-semibold text-purple-700">Client Chronicles & Impact</span>
                            </div>
                            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
                                Feedback From Our<br/>
                                <span className="gradient-text">Verified Placements</span>
                            </h2>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                Read reflections from top candidates who accelerated their pursuits through HarshalJobs
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {[
                                {
                                    quote: "The cognitive skills matching is highly precise. I received multiple interview invitations and secured an executive role within days.",
                                    name: "Rajesh Kumar",
                                    role: "Software Engineer",
                                    company: "TCS",
                                    initials: "RK",
                                    color: "purple",
                                    rating: 5
                                },
                                {
                                    quote: "Starting out, matching my skillset correctly was critical. This platform aligned me with suitable openings and provided direct guidance.",
                                    name: "Priya Sharma",
                                    role: "Marketing Manager",
                                    company: "Infosys",
                                    initials: "PS",
                                    color: "blue",
                                    rating: 5
                                },
                                {
                                    quote: "Unified submission saved hours of redundant data entry. The live tracking panel provided complete transparency throughout the search.",
                                    name: "Amit Patel",
                                    role: "Data Analyst",
                                    company: "Wipro",
                                    initials: "AP",
                                    color: "green",
                                    rating: 5
                                }
                            ].map((testimonial, idx) => (
                                <div 
                                    key={idx} 
                                    className="group relative bg-white rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                                >
                                    <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-${testimonial.color}-400 to-${testimonial.color}-600`}></div>
                                    
                                    <div className="p-6 pt-8">
                                        <div className="flex gap-1 mb-5">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <svg key={i} className={`w-5 h-5 text-${testimonial.color}-400 fill-current`} viewBox="0 0 24 24">
                                                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                                </svg>
                                            ))}
                                        </div>

                                        <div className="mb-4">
                                            <svg className={`w-8 h-8 text-${testimonial.color}-200 opacity-60`} fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                            </svg>
                                        </div>

                                        <p className="text-slate-600 leading-relaxed mb-6 text-sm sm:text-base">
                                            "{testimonial.quote}"
                                        </p>

                                        <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${testimonial.color}-100 to-${testimonial.color}-200 flex items-center justify-center text-${testimonial.color}-700 font-bold text-base shadow-sm`}>
                                                {testimonial.initials}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-bold text-slate-800 text-base">{testimonial.name}</h4>
                                                <div className="flex items-center gap-1 flex-wrap">
                                                    <span className="text-sm text-slate-500">{testimonial.role}</span>
                                                    <span className="text-slate-300 text-xs">•</span>
                                                    <span className={`text-sm font-medium text-${testimonial.color}-600`}>{testimonial.company}</span>
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                                                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FOUNDER STORY SECTION WITH PREMIUM HORIZONTAL SCROLLABLE VISION & MISSION CARDS */}
                <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-purple-100/30 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/30 rounded-full filter blur-3xl"></div>
                    
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div className="group relative">
                                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 rounded-3xl opacity-75 group-hover:opacity-100 blur-md transition duration-500"></div>
                                <div className="relative bg-white rounded-3xl p-8 shadow-2xl text-center">
                                    <div className="relative">
                                        <div className="relative w-36 h-36 mx-auto mb-6">
                                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 animate-spin-slow" style={{ padding: '3px' }}></div>
                                            <div className="relative w-full h-full rounded-full bg-white p-1">
                                                <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-5xl font-bold text-white">
                                                    <img src="/founder.jpeg" alt="Founder" className="w-full h-full rounded-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = 'R'; }} />
                                                </div>
                                            </div>
                                        </div>
                                        <h3 className="text-2xl font-bold text-slate-800 mb-1">Rohit P Kokil</h3>
                                        <p className="text-purple-600 font-medium mb-4">Founder & CEO, HarshalJobs</p>
                                        <div className="flex justify-center gap-6 mb-6">
                                            <div className="text-center">
                                                <div className="text-xl font-bold text-slate-800">5+</div>
                                                <div className="text-xs text-slate-500">Years Exp</div>
                                            </div>
                                            <div className="w-px h-8 bg-slate-200"></div>
                                            <div className="text-center">
                                                <div className="text-xl font-bold text-slate-800">10k+</div>
                                                <div className="text-xs text-slate-500">Lives Impacted</div>
                                            </div>
                                            <div className="w-px h-8 bg-slate-200"></div>
                                            <div className="text-center">
                                                <div className="text-xl font-bold text-slate-800">50+</div>
                                                <div className="text-xs text-slate-500">Partners</div>
                                            </div>
                                        </div>
                                        <div className="flex justify-center gap-2 flex-wrap">
                                            {['Visionary', 'Leader', 'Innovator', 'Mentor'].map((tag, i) => (
                                                <span key={i} className="px-3 py-1 text-xs font-semibold bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-full">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 mb-6 shadow-sm">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                                    </span>
                                    <span className="text-sm font-semibold bg-gradient-to-r from-purple-700 to-blue-700 bg-clip-text text-transparent">
                                        Genesis & Legacy
                                    </span>
                                </div>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
                                    Bridging Talent and <span className="gradient-text">Opportunity</span>
                                </h2>
                                <div className="relative mb-8 p-6 bg-gradient-to-r from-purple-50/50 to-blue-50/50 rounded-2xl border border-purple-100">
                                    <div className="absolute -top-3 -left-2 text-6xl text-purple-300 opacity-40 quote-mark">"</div>
                                    <div className="absolute -bottom-3 -right-2 text-6xl text-purple-300 opacity-40 quote-mark rotate-180">"</div>
                                    <p className="text-lg text-slate-700 leading-relaxed relative z-10 pl-4">
                                        <strong className="text-purple-700 font-semibold">HarshalJobs</strong> was built on a simple premise: 
                                        <span className="block mt-2 text-slate-800 font-medium">"Incredible talent exists everywhere, but opportunities don't always reach them."</span>
                                    </p>
                                </div>
                                <p className="text-slate-600 leading-relaxed mb-8 text-base">
                                    We created this platform to bridge the gap, connecting passionate professionals with forward-thinking employers in their own communities. 
                                    Every feature, every partnership, and every success story drives us to make career transformation accessible to all.
                                </p>
                                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                                    <div className="flex -space-x-2">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white flex items-center justify-center text-slate-600 text-xs font-bold">
                                                {String.fromCharCode(64 + i)}
                                            </div>
                                        ))}
                                    </div>
                                    <span className="text-xs text-slate-500">
                                        Trusted by <strong className="text-slate-700">industry leaders</strong> nationwide
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* PREMIUM HORIZONTAL SCROLLABLE VISION & MISSION CARDS */}
                        <div className="mt-20">
                            <div className="text-center mb-12">
                                <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-sm font-semibold mb-4 shadow-sm">
                                    ⚡ Our Core Principles
                                </span>
                                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">
                                    Strategic Mandate &amp; <span className="gradient-text">Principles</span>
                                </h3>
                                <p className="text-slate-500 mt-3">Click on cards to see the magic ✨ | Swipe → to explore more</p>
                            </div>
                            
                            <div className="relative">
                                <button 
                                    className="vision-scroll-btn absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 hidden md:flex"
                                    onClick={() => {
                                        const container = document.getElementById('visionScrollContainer');
                                        if (container) container.scrollBy({ left: -300, behavior: 'smooth' });
                                    }}
                                >
                                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                
                                <button 
                                    className="vision-scroll-btn absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 hidden md:flex"
                                    onClick={() => {
                                        const container = document.getElementById('visionScrollContainer');
                                        if (container) container.scrollBy({ left: 300, behavior: 'smooth' });
                                    }}
                                >
                                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>

                                <div 
                                    id="visionScrollContainer"
                                    className="vision-scroll-container overflow-x-auto overflow-y-hidden"
                                    style={{ scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
                                >
                                    <div className="vision-track" style={{ display: 'flex', gap: '1.5rem', padding: '0.5rem 0.25rem 1rem 0.25rem', width: 'max-content' }}>
                                        {visionCards.map((card) => (
                                            <div 
                                                key={card.id}
                                                className={`premium-vision-card group ${activeVisionCard === card.id ? `card-flash-${card.color}` : ''}`}
                                                style={{ flex: '0 0 auto', width: '300px' }}
                                                onClick={() => handleVisionCardClick(card.id)}
                                            >
                                                <div className="glow-effect"></div>
                                                <div className="relative p-6 text-center">
                                                    <div className="flex justify-center mb-5">
                                                        <div className={`premium-icon-wrapper bg-gradient-to-br from-${card.color}-500 to-${card.color}-600 shadow-lg`}>
                                                            <span className="text-3xl">{card.icon}</span>
                                                        </div>
                                                    </div>
                                                    <h4 className="premium-title text-xl font-bold mb-3">{card.title}</h4>
                                                    <p className="premium-description text-sm leading-relaxed">{card.description}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-center mt-8 gap-3">
                                {[0, 1, 2, 3].map((idx) => (
                                    <button
                                        key={idx}
                                        className="w-2 h-2 rounded-full transition-all duration-300 hover:scale-150"
                                        style={{ 
                                            backgroundColor: idx === 0 ? '#8B5CF6' : '#cbd5e1',
                                            width: idx === 0 ? '28px' : '8px'
                                        }}
                                        onClick={() => {
                                            const container = document.getElementById('visionScrollContainer');
                                            if (container) container.scrollTo({ left: idx * 310, behavior: 'smooth' });
                                        }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* PREMIUM STATS CARDS - REDESIGNED */}
                        <div className="grid sm:grid-cols-4 gap-6 mt-16">
                            {[
                                { value: '10,000+', label: 'Professionals connected', color: 'purple', icon: '👥', gradient: 'from-purple-500 to-purple-600', bgGradient: 'from-purple-50 to-purple-100' },
                                { value: '1,000+', label: 'Businesses empowered', color: 'green', icon: '🏢', gradient: 'from-green-500 to-green-600', bgGradient: 'from-green-50 to-green-100' },
                                { value: '98%', label: 'Success rate', color: 'blue', icon: '🎯', gradient: 'from-blue-500 to-blue-600', bgGradient: 'from-blue-50 to-blue-100' },
                                { value: '24/7', label: 'Expert support', color: 'orange', icon: '⚡', gradient: 'from-orange-500 to-orange-600', bgGradient: 'from-orange-50 to-orange-100' }
                            ].map((stat, idx) => (
                                <div key={idx} className="premium-stat-card group text-center p-6 bg-white transition-all duration-300">
                                    <div className="stat-badge bg-gradient-to-r from-purple-100 to-blue-100">
                                        <span className="text-xs">✨</span>
                                    </div>
                                    <div className="flex flex-col items-center">
                                        <div className={`stat-icon-wrapper bg-gradient-to-br ${stat.bgGradient} mb-4 shadow-md`}>
                                            <span className="text-3xl">{stat.icon}</span>
                                        </div>
                                        <div className={`stat-value text-3xl font-extrabold mb-2 bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                                            {stat.value}
                                        </div>
                                        <div className="stat-label text-slate-500 text-sm font-medium">
                                            {stat.label}
                                        </div>
                                        <div className="mt-3 w-8 h-0.5 bg-slate-200 rounded-full group-hover:w-12 group-hover:bg-purple-400 transition-all duration-300"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA SECTION */}
                <section className="py-24 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#2E1065]">
                        <div className="absolute inset-0">
                            <div className="floating-particle w-64 h-64 top-10 left-10 animate-float opacity-30"></div>
                            <div className="floating-particle w-96 h-96 bottom-10 right-10 animate-float-slow opacity-20"></div>
                            <div className="floating-particle w-48 h-48 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin-slow opacity-10"></div>
                            
                            <div className="absolute inset-0" style={{ 
                                backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)`,
                                backgroundSize: '40px 40px'
                            }}></div>
                            
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/30 rounded-full filter blur-[100px]"></div>
                        </div>
                    </div>

                    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fadeInUp">
                                Ready to Transform Your Career?
                            </h2>
                            
                            <p className="text-lg text-purple-100 max-w-2xl mx-auto mb-4 leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
                                Join <span className="font-semibold text-white">3,000+ professionals</span> who are already using our platform to advance their careers
                            </p>

                            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-12 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                                <Link 
                                    to="/register" 
                                    className="group relative inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-purple-500/25"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 animate-gradient bg-[length:200%_200%]"></div>
                                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur opacity-0 group-hover:opacity-50 transition duration-300 -z-10"></div>
                                    <span className="relative flex items-center gap-2">
                                        Create Your Free Account
                                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7"/>
                                        </svg>
                                    </span>
                                </Link>
                                
                                <Link 
                                    to="/login" 
                                    className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:border-white/50 hover:scale-105 backdrop-blur-sm"
                                >
                                    <span className="relative flex items-center gap-2">
                                        Sign In
                                        <svg className="w-5 h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7"/>
                                        </svg>
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Professional Footer with Clickable Social Icons */}
            <footer className="bg-gradient-to-b from-slate-900 to-slate-950 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
                        <div className="lg:col-span-1">
                            <div className="mb-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                                        <span className="text-white font-bold text-xl">HJ</span>
                                    </div>
                                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                                        HarshalJobs
                                    </span>
                                </div>
                                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                                    India's most trusted career platform connecting talented professionals with leading employers.
                                </p>
                                <div className="flex gap-3">
                                    {/* Twitter / X */}
                                    <button 
                                        onClick={() => handleSocialClick(socialLinks.twitter)}
                                        className="social-icon w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-purple-500 transition-all duration-300 group"
                                        aria-label="Follow us on Twitter"
                                    >
                                        <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                                        </svg>
                                    </button>
                                    {/* LinkedIn */}
                                    <button 
                                        onClick={() => handleSocialClick(socialLinks.linkedin)}
                                        className="social-icon w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-purple-500 transition-all duration-300 group"
                                        aria-label="Connect with us on LinkedIn"
                                    >
                                        <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M4.98 3.5C4.98 4.604 4.104 5.5 3 5.5S1.02 4.604 1.02 3.5 1.896 1.5 3 1.5s1.98.896 1.98 2zM1.5 8h3V22h-3V8zm7 0h2.879v1.91h.041c.401-.761 1.379-1.562 2.839-1.562 3.037 0 3.6 2 3.6 4.59V22h-3v-6.5c0-1.55-.028-3.54-2.158-3.54-2.16 0-2.49 1.688-2.49 3.43V22h-3V8z" />
                                        </svg>
                                    </button>
                                    {/* GitHub */}
                                    <button 
                                        onClick={() => handleSocialClick(socialLinks.github)}
                                        className="social-icon w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-purple-500 transition-all duration-300 group"
                                        aria-label="Check our GitHub"
                                    >
                                        <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 0c-6.627 0-12 5.373-12 12 0 5.302 3.438 9.8 8.205 11.387.6.113.82-.26.82-.58 0-.287-.01-1.05-.015-2.06-3.338.726-4.042-1.416-4.042-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.108-.775.418-1.305.762-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.468-2.381 1.235-3.22-.123-.3-.535-1.52.117-3.16 0 0 1.008-.322 3.3 1.23.96-.267 1.98-.4 3-.405 1.02.005 2.04.138 3 .405 2.29-1.552 3.297-1.23 3.297-1.23.653 1.64.241 2.86.118 3.16.768.839 1.233 1.91 1.233 3.22 0 4.61-2.804 5.62-5.476 5.92.43.37.824 1.102.824 2.22 0 1.602-.015 2.894-.015 3.287 0 .322.216.698.83.578 4.765-1.588 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                        </svg>
                                    </button>
                                    {/* Instagram */}
                                    <button 
                                        onClick={() => handleSocialClick(socialLinks.instagram)}
                                        className="social-icon w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-purple-500 transition-all duration-300 group"
                                        aria-label="Follow us on Instagram"
                                    >
                                        <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
                            <ul className="space-y-2">
                                {[
                                    { name: 'Find Jobs', path: '/jobs' },
                                    { name: 'Browse Companies', path: '/companies' },
                                    { name: 'Success Stories', path: '/success-stories' },
                                    { name: 'Career Resources', path: '/resources' },
                                    { name: 'Salary Guide', path: '/salary-guide' }
                                ].map((item, idx) => (
                                    <li key={idx}>
                                        <Link to={item.path} className="footer-link text-slate-400 text-sm hover:text-purple-400 transition-all duration-200 inline-block">
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-white font-semibold text-lg mb-4">For Job Seekers</h3>
                            <ul className="space-y-2">
                                {[
                                    { name: 'Create Account', path: '/register' },
                                    { name: 'Job Alerts', path: '/job-alerts' },
                                    { name: 'Resume Builder', path: '/resume-builder' },
                                    { name: 'Interview Tips', path: '/interview-tips' },
                                    { name: 'Career Advice', path: '/career-advice' }
                                ].map((item, idx) => (
                                    <li key={idx}>
                                        <Link to={item.path} className="footer-link text-slate-400 text-sm hover:text-purple-400 transition-all duration-200 inline-block">
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-white font-semibold text-lg mb-4">For Employers</h3>
                            <ul className="space-y-2">
                                {[
                                    { name: 'Post a Job', path: '/post-job' },
                                    { name: 'Company Profile', path: '/company-profile' },
                                    { name: 'Recruitment Solutions', path: '/recruitment' },
                                    { name: 'Pricing Plans', path: '/pricing' },
                                    { name: 'Contact Sales', path: '/contact-sales' }
                                ].map((item, idx) => (
                                    <li key={idx}>
                                        <Link to={item.path} className="footer-link text-slate-400 text-sm hover:text-purple-400 transition-all duration-200 inline-block">
                                            {item.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-white font-semibold text-lg mb-4">Get in Touch</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                    <span className="text-slate-400 text-sm">support@harshaljobs.com</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                                    </svg>
                                    <span className="text-slate-400 text-sm">+91 98765 43210</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <svg className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                    </svg>
                                    <span className="text-slate-400 text-sm">Mumbai, India</span>
                                </li>
                            </ul>
                            
                            <div className="mt-6">
                                <h4 className="text-white text-sm font-semibold mb-2">Subscribe to Newsletter</h4>
                                <div className="flex">
                                    <input 
                                        type="email" 
                                        placeholder="Your email" 
                                        className="flex-1 px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-l-lg focus:outline-none focus:border-purple-500 text-white placeholder-slate-500"
                                    />
                                    <button className="px-3 py-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-r-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-300">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-white/10">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                            <p className="text-slate-500 text-sm">
                                © {new Date().getFullYear()} HarshalJobs. All rights reserved.
                            </p>
                            <div className="flex gap-6">
                                <Link to="/privacy" className="footer-link text-slate-500 text-sm hover:text-purple-400 transition-all duration-200">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms" className="footer-link text-slate-500 text-sm hover:text-purple-400 transition-all duration-200">
                                    Terms of Service
                                </Link>
                                <Link to="/cookie" className="footer-link text-slate-500 text-sm hover:text-purple-400 transition-all duration-200">
                                    Cookie Policy
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default LandingPage;